class DesignStorage {
  constructor() {
    this.LIMIT = 3; // Max designs per product

    // Safety check: Prevents the entire class from crashing if the CDN/Library fails to load
    if (typeof localforage === "undefined") {
      console.error(
        "DesignStorage Initialization Failed: 'localforage' is not defined.",
      );
      this.isReady = false;
      return;
    }

    try {
      this.designStore = localforage.createInstance({
        name: "ShopifyDYO",
        storeName: "customDesigns",
      });
      this.isReady = true;
    } catch (error) {
      console.error(
        "DesignStorage: Failed to create IndexedDB instance.",
        error,
      );
      this.isReady = false;
    }
  }

  /**
   * Safely retrieves designs for a given product.
   */
  async get(productId) {
    if (!this.isReady || !productId) return [];

    try {
      const designs = await this.designStore.getItem(productId);
      return Array.isArray(designs) ? designs : [];
    } catch (error) {
      console.error(
        `DesignStorage: Failed to read designs for product ${productId}.`,
        error,
      );
      return []; // Always return an array to prevent .map() or .length errors down the line
    }
  }

  /**
   * Saves or updates a design configuration.
   */
  async save(productId, config, id = null, imageUrl = null, additionalData = {}) {
    if (!this.isReady || !productId || !config) {
      console.warn("DesignStorage: Missing required parameters for save().");
      return null;
    }

    try {
      let designs = await this.get(productId);
      const designId = id || (config && config.id) || `design_${Date.now()}`;

      const newEntry = {
        id: designId,
        productId,
        config,
        imageUrl,
        lastUpdated: new Date().toISOString(),
        ...additionalData
      };

      const existingIndex = designs.findIndex((d) => d.id === designId);

      if (existingIndex !== -1) {
        // Update existing entry
        designs[existingIndex] = { ...designs[existingIndex], ...newEntry };
      } else {
        // Add new entry to the front
        designs.unshift(newEntry);

        // Enforce limits using slice (faster and safer than pop() in loops)
        if (designs.length > this.LIMIT) {
          designs = designs.slice(0, this.LIMIT);
        }
      }

      await this.designStore.setItem(productId, designs);

      // Notify the rest of the application
      window.dispatchEvent(
        new CustomEvent("designs:updated", {
          detail: { productId, designId },
        }),
      );

      return designId;
    } catch (error) {
      console.error(
        "DesignStorage: Critical failure during save operation.",
        error,
      );
      return null;
    }
  }

  /**
   * Deletes a specific design by ID.
   */
  async delete(productId, id) {
    if (!this.isReady || !productId || !id) return;

    try {
      const designs = await this.get(productId);

      // Only write to the DB if we actually have data to filter
      if (designs.length > 0) {
        const filteredDesigns = designs.filter((d) => d.id !== id);
        await this.designStore.setItem(productId, filteredDesigns);

        // Notify the rest of the application
        window.dispatchEvent(
          new CustomEvent("designs:deleted", {
            detail: { productId, designId: id },
          }),
        );        
      }
    } catch (error) {
      console.error(`DesignStorage: Failed to delete design ${id}.`, error);
    }
  }

  /**
   * Caches Shopify section HTML for a design in the same DB as customDesigns.
   */
  async getSectionCache(designId) {
    if (!this.isReady || !designId) return null;
    try {
      const cacheKey = `sectionCache_${designId}`;
      return await this.designStore.getItem(cacheKey);
    } catch (error) {
      console.error(
        `DesignStorage: Failed to get section cache for ${designId}.`,
        error,
      );
      return null;
    }
  }

  async setSectionCache(designId, html) {
    if (!this.isReady || !designId || !html) return;
    try {
      const cacheKey = `sectionCache_${designId}`;
      await this.designStore.setItem(cacheKey, {
        html,
        createdAt: Date.now(),
      });
    } catch (error) {
      console.error(
        `DesignStorage: Failed to set section cache for ${designId}.`,
        error,
      );
    }
  }

  async clearSectionCache(designId) {
    if (!this.isReady || !designId) return;
    try {
      const cacheKey = `sectionCache_${designId}`;
      await this.designStore.removeItem(cacheKey);
    } catch (error) {
      console.error(
        `DesignStorage: Failed to clear section cache for ${designId}.`,
        error,
      );
    }
  }

  /**
   * Wipes all DYO configurations from IndexedDB.
   */
  async clearAll() {
    if (!this.isReady) return;

    try {
      await this.designStore.clear();
      console.info("DesignStorage: All custom designs cleared.");
    } catch (error) {
      console.error("DesignStorage: Failed to clear storage.", error);
    }
  }
}

// Expose to window for standard Shopify theme usage
window.DesignStorage = DesignStorage;
